import { useState, useEffect, type ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Search, Upload, RefreshCw, Users, ShieldOff, ShieldCheck, MailX, Mail } from 'lucide-react';
import toast from 'react-hot-toast';
import { useProductClients, useProductClientStats } from '../hooks/useProductClients';
import { useProductPlans } from '../hooks/useProductPlans';
import { useAccessibleProducts } from '../hooks/useAccessibleProducts';
import { productClientsService } from '../services/product-clients.service';
import { useAuth } from '../contexts/AuthContext';
import { LoadingSpinner } from '../components/common/LoadingSpinner';
import { ProductSelector } from '../components/common/ProductSelector';

// Tooltip que aparece imediatamente no hover (o `title` nativo é lento e discreto).
function IconTooltip({ label, children }: { label: string; children: ReactNode }) {
  return (
    <span className="relative inline-flex group align-middle">
      {children}
      <span
        role="tooltip"
        className="pointer-events-none absolute right-0 top-full mt-1 z-20 hidden group-hover:block max-w-[220px] whitespace-normal text-left rounded-md bg-ink-primary px-2 py-1 text-xs font-normal leading-snug text-canvas shadow-lg"
      >
        {label}
      </span>
    </span>
  );
}

export function ProductClientsPage() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const isAdmin = user?.role === 'ADMIN';

  const { data: products = [] } = useAccessibleProducts();
  const [searchParams, setSearchParams] = useSearchParams();

  const urlProductId = searchParams.get('productId');
  const defaultProductId = products[0]?.id ?? null;
  const productId = urlProductId ? Number(urlProductId) : defaultProductId;

  useEffect(() => {
    if (!urlProductId && defaultProductId) {
      setSearchParams({ productId: String(defaultProductId) }, { replace: true });
    }
  }, [urlProductId, defaultProductId, setSearchParams]);

  const [search, setSearch] = useState('');
  const [planId, setPlanId] = useState<number | ''>('');
  const [systemAccounts, setSystemAccounts] = useState<'hide' | 'include' | 'only'>('hide');
  const [onlyOptedOut, setOnlyOptedOut] = useState(false);
  const [page, setPage] = useState(1);

  const { data: plans = [] } = useProductPlans(productId);
  const product = products.find((p) => p.id === productId) ?? null;

  const { data, isLoading } = useProductClients({
    productId: productId ?? 0,
    search: search || undefined,
    planId: planId === '' ? undefined : planId,
    page,
    limit: 20,
    systemAccounts,
    optOut: onlyOptedOut ? 'only' : 'all',
  });

  const { data: stats } = useProductClientStats(productId);

  const handleProductChange = (id: number) => {
    setSearchParams({ productId: String(id) }, { replace: false });
    setPlanId('');
    setPage(1);
  };

  const syncMutation = useMutation({
    mutationFn: () => productClientsService.sync(productId as number),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['product-clients'] });
      toast.success('Sincronização concluída');
    },
    onError: (e: any) => {
      const msg = e?.response?.data?.error ?? e?.message ?? 'Erro ao sincronizar';
      toast.error(msg, { duration: 8000 });
    },
  });

  const importMutation = useMutation({
    mutationFn: (file: File) => productClientsService.import(productId as number, file),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['product-clients'] });
      toast.success('Importação realizada');
    },
    onError: () => toast.error('Erro ao importar'),
  });

  const updateMutation = useMutation({
    mutationFn: ({
      id,
      isSystemAccount,
      marketingOptOut,
    }: {
      id: number;
      isSystemAccount?: boolean;
      marketingOptOut?: boolean;
    }) => productClientsService.update(productId as number, id, { isSystemAccount, marketingOptOut }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['product-clients'] });
      toast.success('Cliente atualizado');
    },
    onError: () => toast.error('Erro ao atualizar cliente'),
  });

  const handleImportCSV = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.csv';
    input.onchange = (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (file) {
        importMutation.mutate(file);
      }
    };
    input.click();
  };

  const clients: any[] = data?.clients ?? data?.data ?? [];
  const totalClients: number = data?.total ?? 0;
  const totalPages: number = data?.totalPages ?? 1;
  const byPlan: Record<number, number> = stats?.byPlan ?? {};

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-bold text-ink-primary">Clientes</h1>
          <p className="text-ink-secondary">
            {product && totalClients > 0
              ? `${totalClients} cliente${totalClients !== 1 ? 's' : ''} em ${product.name}`
              : product
                ? `Sem clientes em ${product.name}`
                : 'Selecione um produto'}
          </p>
        </div>
        {isAdmin && productId && (
          <div className="flex items-center gap-2">
            <button
              onClick={() => syncMutation.mutate()}
              disabled={syncMutation.isPending}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium border border-border text-ink-secondary bg-surface rounded-lg hover:bg-subtle disabled:opacity-50"
            >
              <RefreshCw className={`w-4 h-4 ${syncMutation.isPending ? 'animate-spin' : ''}`} />
              Sincronizar
            </button>
            <button
              onClick={handleImportCSV}
              disabled={importMutation.isPending}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium bg-ink-primary text-canvas rounded-lg hover:bg-ink-secondary disabled:opacity-50"
            >
              <Upload className="w-4 h-4" />
              {importMutation.isPending ? 'Importando...' : 'Importar CSV'}
            </button>
          </div>
        )}
      </div>

      {plans.length > 0 && (
        <div
          className="grid gap-4"
          style={{ gridTemplateColumns: `repeat(${Math.min(plans.length, 4)}, minmax(0, 1fr))` }}
        >
          {plans.map((plan) => (
            <div
              key={plan.id}
              className="bg-surface rounded-[14px] border border-border p-5 flex items-center gap-4"
            >
              <div className="w-10 h-10 rounded-full bg-subtle flex items-center justify-center text-info font-semibold">
                {plan.name.charAt(0).toUpperCase()}
              </div>
              <div>
                <p className="text-2xl font-bold text-ink-primary">{byPlan[plan.id] ?? 0}</p>
                <p className="text-sm text-ink-tertiary">{plan.name}</p>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="bg-surface rounded-[14px] border border-border p-4">
        <div className="flex flex-wrap items-center gap-4">
          {products.length > 1 && (
            <ProductSelector value={productId} onChange={handleProductChange} />
          )}
          <div className="relative flex-1 min-w-[200px] max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-tertiary" />
            <input
              type="text"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
              placeholder="Buscar por nome, e-mail ou empresa..."
              className="input pl-10 w-full"
            />
          </div>
          <select
            value={planId}
            onChange={(e) => {
              setPlanId(e.target.value === '' ? '' : Number(e.target.value));
              setPage(1);
            }}
            className="input w-auto"
          >
            <option value="">Todos os planos</option>
            {plans.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </select>
          {isAdmin && (
            <select
              value={systemAccounts}
              onChange={(e) => {
                setSystemAccounts(e.target.value as 'hide' | 'include' | 'only');
                setPage(1);
              }}
              className="input w-auto"
              title="Visibilidade das contas de sistema"
            >
              <option value="hide">Ocultar contas de sistema</option>
              <option value="include">Incluir contas de sistema</option>
              <option value="only">Somente contas de sistema</option>
            </select>
          )}
          {isAdmin && (
            <label className="flex items-center gap-2 text-sm text-ink-secondary cursor-pointer">
              <input
                type="checkbox"
                checked={onlyOptedOut}
                onChange={(e) => {
                  setOnlyOptedOut(e.target.checked);
                  setPage(1);
                }}
              />
              Somente opt-out
            </label>
          )}
        </div>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center h-64">
          <LoadingSpinner size="lg" />
        </div>
      ) : clients.length === 0 ? (
        <div className="bg-surface rounded-[14px] border border-border text-center py-16">
          <Users className="w-12 h-12 text-ink-tertiary mx-auto mb-4" />
          <p className="text-ink-secondary">Nenhum cliente encontrado</p>
        </div>
      ) : (
        <div className="bg-surface rounded-[14px] border border-border overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border bg-canvas">
                  <th className="text-left px-4 py-3 font-medium text-ink-secondary">Nome</th>
                  <th className="text-left px-4 py-3 font-medium text-ink-secondary">Email</th>
                  <th className="text-left px-4 py-3 font-medium text-ink-secondary">Empresa</th>
                  <th className="text-left px-4 py-3 font-medium text-ink-secondary">Plano</th>
                  <th className="text-left px-4 py-3 font-medium text-ink-secondary">Uso</th>
                  <th className="text-left px-4 py-3 font-medium text-ink-secondary">Origem</th>
                  {isAdmin && <th className="w-10"></th>}
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {clients.map((client) => {
                  const isSys = !!client.isSystemAccount;
                  return (
                    <tr key={client.id} className={`hover:bg-subtle ${isSys ? 'opacity-60' : ''}`}>
                      <td className="px-4 py-3 font-medium text-ink-primary">
                        <div className="flex items-center gap-2">
                          <span>{client.name || '—'}</span>
                          {isSys && (
                            <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-subtle text-ink-secondary uppercase">
                              Sistema
                            </span>
                          )}
                          {client.marketingOptOut && (
                            <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-warning/15 text-warning uppercase">
                              Opt-out
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-ink-secondary">{client.email}</td>
                      <td className="px-4 py-3 text-ink-secondary">{client.company || '—'}</td>
                      <td className="px-4 py-3">
                        {client.plan ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-subtle text-info">
                            {client.plan.name}
                          </span>
                        ) : (
                          <span className="text-ink-tertiary text-xs">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-ink-secondary">
                        {client.usageCount ?? 0}
                        {client.usageLimit ? ` / ${client.usageLimit}` : ''}
                      </td>
                      <td className="px-4 py-3 text-ink-tertiary text-xs">
                        {client.importSource || '—'}
                      </td>
                      {isAdmin && (
                        <td className="px-4 py-3 text-right whitespace-nowrap">
                          <IconTooltip
                            label={
                              client.marketingOptOut
                                ? 'Reativar recebimento de marketing (volta a aparecer nas campanhas)'
                                : 'Marcar opt-out de marketing (deixa de receber e some das campanhas)'
                            }
                          >
                            <button
                              type="button"
                              onClick={() =>
                                updateMutation.mutate({
                                  id: client.id,
                                  marketingOptOut: !client.marketingOptOut,
                                })
                              }
                              disabled={updateMutation.isPending}
                              aria-label={
                                client.marketingOptOut
                                  ? 'Reativar recebimento de marketing'
                                  : 'Marcar opt-out de marketing'
                              }
                              className="p-1.5 rounded-md text-ink-tertiary hover:text-ink-primary hover:bg-canvas disabled:opacity-50"
                            >
                              {client.marketingOptOut ? (
                                <Mail className="w-4 h-4" />
                              ) : (
                                <MailX className="w-4 h-4" />
                              )}
                            </button>
                          </IconTooltip>
                          <IconTooltip
                            label={
                              isSys
                                ? 'Remover marcação de conta de sistema'
                                : 'Marcar como conta de sistema (oculta de estatísticas e campanhas)'
                            }
                          >
                            <button
                              type="button"
                              onClick={() =>
                                updateMutation.mutate({ id: client.id, isSystemAccount: !isSys })
                              }
                              disabled={updateMutation.isPending}
                              aria-label={
                                isSys
                                  ? 'Remover marcação de conta de sistema'
                                  : 'Marcar como conta de sistema'
                              }
                              className="p-1.5 rounded-md text-ink-tertiary hover:text-ink-primary hover:bg-canvas disabled:opacity-50"
                            >
                              {isSys ? (
                                <ShieldOff className="w-4 h-4" />
                              ) : (
                                <ShieldCheck className="w-4 h-4" />
                              )}
                            </button>
                          </IconTooltip>
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2">
          <button
            onClick={() => setPage((p) => p - 1)}
            disabled={page === 1}
            className="px-4 py-2 text-sm border border-border text-ink-secondary bg-surface rounded-lg hover:bg-subtle disabled:opacity-40"
          >
            Anterior
          </button>
          <span className="text-sm text-ink-secondary">
            Página {page} de {totalPages}
          </span>
          <button
            onClick={() => setPage((p) => p + 1)}
            disabled={page === totalPages}
            className="px-4 py-2 text-sm border border-border text-ink-secondary bg-surface rounded-lg hover:bg-subtle disabled:opacity-40"
          >
            Próxima
          </button>
        </div>
      )}
    </div>
  );
}

export default ProductClientsPage;
